import {i18n} from "../../shared/i18n";

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;
const MAX_AVATAR_PIXELS = 25_000_000;

const MIME_BY_FORMAT = {
    JPEG: "image/jpeg",
    PNG: "image/png",
    WEBP: "image/webp",
};

const invalidImage = () => new Error(i18n.t("orgs.avatar.invalid"));

const readFileBuffer = (file) => {
    if (typeof file.arrayBuffer === "function") {
        return file.arrayBuffer().catch(() => Promise.reject(invalidImage()));
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        const release = () => {
            reader.onload = null;
            reader.onerror = null;
            reader.onabort = null;
        };
        reader.onload = () => {
            const result = reader.result;
            release();
            resolve(result);
        };
        reader.onerror = () => {
            release();
            reject(invalidImage());
        };
        reader.onabort = () => {
            release();
            reject(invalidImage());
        };
        try {
            reader.readAsArrayBuffer(file);
        } catch (_error) {
            release();
            reject(invalidImage());
        }
    });
};

const matches = (bytes, offset, expected) => expected.every((value, index) => bytes[offset + index] === value);

const imageFormat = (bytes) => {
    if (bytes.length >= 3 && matches(bytes, 0, [0xff, 0xd8, 0xff])) return "JPEG";
    if (bytes.length >= 24 && matches(bytes, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
        return "PNG";
    }
    if (bytes.length >= 30 && matches(bytes, 0, [0x52, 0x49, 0x46, 0x46]) && matches(bytes, 8, [0x57, 0x45, 0x42, 0x50])) {
        return "WEBP";
    }
    return null;
};

const jpegDimensions = (bytes) => {
    const frameMarkers = new Set([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf]);
    let offset = 2;
    while (offset + 8 < bytes.length) {
        if (bytes[offset] !== 0xff) {
            offset += 1;
            continue;
        }
        while (bytes[offset] === 0xff) offset += 1;
        const marker = bytes[offset];
        offset += 1;
        if (marker === 0xd8 || marker === 0xd9 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;
        if (offset + 1 >= bytes.length) return null;
        const length = (bytes[offset] << 8) + bytes[offset + 1];
        if (length < 2 || offset + length > bytes.length) return null;
        if (frameMarkers.has(marker) && length >= 7) {
            return {
                height: (bytes[offset + 3] << 8) + bytes[offset + 4],
                width: (bytes[offset + 5] << 8) + bytes[offset + 6],
            };
        }
        offset += length;
    }
    return null;
};

const webpDimensions = (bytes) => {
    const chunk = String.fromCharCode(...bytes.slice(12, 16));
    if (chunk === "VP8X" && bytes.length >= 30) {
        return {
            width: 1 + bytes[24] + (bytes[25] << 8) + (bytes[26] << 16),
            height: 1 + bytes[27] + (bytes[28] << 8) + (bytes[29] << 16),
        };
    }
    if (chunk === "VP8L" && bytes.length >= 25 && bytes[20] === 0x2f) {
        const bits = bytes[21] | (bytes[22] << 8) | (bytes[23] << 16) | (bytes[24] << 24);
        return {width: (bits & 0x3fff) + 1, height: ((bits >>> 14) & 0x3fff) + 1};
    }
    if (chunk === "VP8 " && bytes.length >= 30 && matches(bytes, 23, [0x9d, 0x01, 0x2a])) {
        return {
            width: (bytes[26] | (bytes[27] << 8)) & 0x3fff,
            height: (bytes[28] | (bytes[29] << 8)) & 0x3fff,
        };
    }
    return null;
};

const headerDimensions = (format, bytes) => {
    if (format === "PNG") {
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        return {width: view.getUint32(16), height: view.getUint32(20)};
    }
    if (format === "JPEG") return jpegDimensions(bytes);
    return webpDimensions(bytes);
};

const imageElementDimensions = (file) => {
    if (
        typeof Image !== "function"
        || typeof URL?.createObjectURL !== "function"
        || typeof URL?.revokeObjectURL !== "function"
    ) {
        return null;
    }
    const objectUrl = URL.createObjectURL(file);
    return new Promise((resolve, reject) => {
        let image;
        try {
            image = new Image();
        } catch (_error) {
            URL.revokeObjectURL(objectUrl);
            reject(invalidImage());
            return;
        }
        const release = () => {
            try {
                image.onload = null;
                image.onerror = null;
                image.src = "";
            } finally {
                URL.revokeObjectURL(objectUrl);
            }
        };
        image.onload = () => {
            const dimensions = {width: image.naturalWidth, height: image.naturalHeight};
            release();
            resolve(dimensions);
        };
        image.onerror = () => {
            release();
            reject(invalidImage());
        };
        try {
            image.src = objectUrl;
        } catch (_error) {
            release();
            reject(invalidImage());
        }
    });
};

const browserDimensions = async (file) => {
    if (typeof window.createImageBitmap === "function") {
        let bitmap;
        try {
            bitmap = await window.createImageBitmap(file);
            return {width: bitmap.width, height: bitmap.height};
        } catch (_error) {
            const dimensions = imageElementDimensions(file);
            if (dimensions) return dimensions;
            throw invalidImage();
        } finally {
            bitmap?.close?.();
        }
    }
    return imageElementDimensions(file);
};

export const validateStaffAvatar = async (file) => {
    if (!file) throw new Error(i18n.t("orgs.avatar.choose"));
    if (file.size > MAX_AVATAR_BYTES) throw new Error(i18n.t("orgs.avatar.tooLarge"));
    if (!Object.values(MIME_BY_FORMAT).includes(file.type)) {
        throw new Error(i18n.t("orgs.avatar.formats"));
    }

    const buffer = await readFileBuffer(file);
    const bytes = new Uint8Array(buffer);
    const format = imageFormat(bytes);
    if (!format || MIME_BY_FORMAT[format] !== file.type) throw invalidImage();

    const decodedDimensions = await browserDimensions(file);
    const dimensions = decodedDimensions || headerDimensions(format, bytes);
    if (!dimensions || dimensions.width <= 0 || dimensions.height <= 0) throw invalidImage();
    if (dimensions.width * dimensions.height > MAX_AVATAR_PIXELS) {
        throw new Error(i18n.t("orgs.avatar.tooBig"));
    }
    return file;
};
